// frontend/src/components/homepage/artefacts/AiBrain.js
//
// Tile 01's artefact: the brain outline with short glowing currents (teal ->
// pink) that run along its folds at random. A literal port of the prototype's
// `.art-ai .brain` SVG and its "brain: short glowing currents" script block.
//
// HOW A CURRENT RUNS. Each of the 15 fold paths is rendered TWICE: a wide
// gradient stroke under the glow filter and a hairline white core on top. Both
// carry `stroke-dasharray: 16 260` at offset 276, i.e. a 16-unit dash parked
// off the far end of the path. "Firing" a pair sets `--hl-ft-d` to a random
// 1.0-1.7s, removes the `hl-ft-run` class, forces a reflow and re-adds it - the
// prototype's own restart trick - so `hl-curRun` (dashoffset -> 0) replays from
// the start. Up to 5 pairs are live at once; the scheduler wakes every
// 120-320ms and fires one more if there is room.
//
// The class is toggled IMPERATIVELY on the path elements rather than through
// React state: the restart depends on a synchronous remove -> reflow -> add on
// the same element, which no state round-trip can promise, and 30 paths
// re-rendering every 120ms for a decoration would be the wrong trade anyway.
//
// WHEN IT RUNS. `useInViewGate` (threshold .1, the prototype's observer) AND
// `useMotion()`: `off` (the a11y toggle / OS preference) stops it, `paused`
// (tab hidden) suspends it, and `window.__PRERENDER__` never starts it. Under
// motion-off the brain fires NOTHING - the prototype's reduced-motion branch
// fired three currents once, but every animation is dead under that switch so
// they would only have frozen invisible; the owner's rule is "currents fire
// nothing". On deactivation every `hl-ft-run` is stripped so a mid-run current
// cannot freeze VISIBLE (the `forwards` fill would otherwise hold it).
//
// The <defs> ids are document-global, so they carry the `hlFt` prefix.

import React, { memo, useCallback, useEffect, useRef } from "react";
import useMotion from "../../../motion/useMotion";
import useInViewGate, { isPrerendering } from "../../../motion/useInViewGate";

/** The outline - also the clip for the currents. */
export const BRAIN_OUTLINE =
  "M64 116 C42 114 28 94 34 74 C24 58 36 34 58 32 C66 12 98 6 114 20 C132 6 162 12 170 34 C192 38 198 64 186 80 C196 96 182 116 162 114 C158 128 138 132 128 120 C120 130 102 132 94 122 C84 126 70 124 64 116 Z";

const GYRI =
  "M60 56 C74 48 82 66 68 74 M52 90 C66 82 76 100 62 108 M88 34 C96 46 84 56 94 64 M100 92 C114 84 122 104 108 112 M126 38 C140 32 148 50 134 58 M144 74 C158 68 164 88 150 96 M120 66 C134 60 140 78 126 86 M80 104 C94 98 100 116 86 122 M160 44 C170 52 160 62 168 70 M110 42 C118 54 106 62 114 72";

const CEREB = "M128 120 C134 106 156 104 162 114 M136 118 C140 110 152 110 156 116";

const STEM = "M110 124 C110 136 106 142 110 150 M100 150 L120 150";

/** The 15 fold paths a current can run along - the prototype's PATHS array. */
export const CURRENT_PATHS = [
  "M60 56 C74 48 82 66 68 74",
  "M52 90 C66 82 76 100 62 108",
  "M88 34 C96 46 84 56 94 64",
  "M100 92 C114 84 122 104 108 112",
  "M126 38 C140 32 148 50 134 58",
  "M144 74 C158 68 164 88 150 96",
  "M120 66 C134 60 140 78 126 86",
  "M80 104 C94 98 100 116 86 122",
  "M160 44 C170 52 160 62 168 70",
  "M110 42 C118 54 106 62 114 72",
  "M48 70 C76 44 116 100 156 62",
  "M66 112 C98 82 132 120 156 92",
  "M58 46 C90 72 112 30 152 52",
  "M92 118 C112 90 142 110 172 94",
  "M40 80 C70 96 96 60 130 100",
];

/** At most this many currents are running at once. */
export const MAX_LIVE_CURRENTS = 5;
/** One current's run time, seconds - uniform in this range. */
export const RUN_MIN_S = 1.0;
export const RUN_MAX_S = 1.7;
/** The scheduler's wake interval, ms - uniform in this range. */
export const LOOP_MIN_MS = 120;
export const LOOP_MAX_MS = 320;
/** Extra ms past a run before its class is stripped, so `forwards` has landed. */
const RUN_SLACK_MS = 60;

export const RUN_CLASS = "hl-ft-run";

const rnd = (a, b) => a + Math.random() * (b - a);

/** Every path is the pair index `i` -> children [2i, 2i+1] of the group. */
function pairsOf(group) {
  const kids = Array.from(group.children);
  const pairs = [];
  for (let i = 0; i + 1 < kids.length; i += 2) pairs.push([kids[i], kids[i + 1]]);
  return pairs;
}

function AiBrain() {
  const { off, paused } = useMotion();
  const [gateRef, inView] = useInViewGate({ threshold: 0.1 });
  const groupRef = useRef(null);

  const active = !off && !paused && inView && !isPrerendering();

  useEffect(() => {
    const group = groupRef.current;
    if (!active || !group) return undefined;

    const pairs = pairsOf(group);
    if (pairs.length === 0) return undefined;

    const timers = new Set();
    const later = (fn, ms) => {
      const id = setTimeout(() => {
        timers.delete(id);
        fn();
      }, ms);
      timers.add(id);
      return id;
    };

    const fire = (pair) => {
      const d = rnd(RUN_MIN_S, RUN_MAX_S).toFixed(2);
      pair.forEach((el) => {
        el.style.setProperty("--hl-ft-d", `${d}s`);
        el.classList.remove(RUN_CLASS);
        // Force a reflow between remove and add - that is what restarts the
        // keyframe on an element that was already running it.
        el.getBoundingClientRect();
        el.classList.add(RUN_CLASS);
      });
      later(() => pair.forEach((el) => el.classList.remove(RUN_CLASS)), parseFloat(d) * 1000 + RUN_SLACK_MS);
    };

    const loop = () => {
      const live = group.querySelectorAll(`.${RUN_CLASS}`).length / 2;
      if (live < MAX_LIVE_CURRENTS) fire(pairs[Math.floor(Math.random() * pairs.length)]);
      later(loop, rnd(LOOP_MIN_MS, LOOP_MAX_MS));
    };
    loop();

    return () => {
      timers.forEach((id) => clearTimeout(id));
      timers.clear();
      pairs.forEach((pair) => pair.forEach((el) => el.classList.remove(RUN_CLASS)));
    };
  }, [active]);

  // One callback ref feeds both the gate and the SVG root.
  const svgRef = useCallback((node) => gateRef(node), [gateRef]);

  return (
    <svg ref={svgRef} className="hl-ft-brain" viewBox="0 0 220 160" aria-hidden="true">
      <defs>
        <linearGradient id="hlFtBrainFill" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#40E0D0" stopOpacity=".14" />
          <stop offset="1" stopColor="#FFB6C1" stopOpacity=".16" />
        </linearGradient>
        <linearGradient id="hlFtBrainLine" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#00A896" />
          <stop offset="1" stopColor="#5AB5E3" />
        </linearGradient>
        <radialGradient id="hlFtHalo">
          <stop offset="0" stopColor="#40E0D0" stopOpacity=".2" />
          <stop offset="1" stopColor="#40E0D0" stopOpacity="0" />
        </radialGradient>
        <filter id="hlFtGlow" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="1.8" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <linearGradient id="hlFtCurG" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#40E0D0" />
          <stop offset=".5" stopColor="#7FEFE6" />
          <stop offset="1" stopColor="#FF8FB8" />
        </linearGradient>
        <linearGradient id="hlFtCurG2" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#FF9EC4" />
          <stop offset=".5" stopColor="#FFD1E0" />
          <stop offset="1" stopColor="#40E0D0" />
        </linearGradient>
        <clipPath id="hlFtBrainClip">
          <path d={BRAIN_OUTLINE} />
        </clipPath>
      </defs>
      <ellipse className="hl-ft-halo" cx="112" cy="78" rx="100" ry="66" />
      <path className="hl-ft-outline" d={BRAIN_OUTLINE} />
      <path className="hl-ft-gyri" d={GYRI} />
      <g ref={groupRef} className="hl-ft-curs" clipPath="url(#hlFtBrainClip)">
        {CURRENT_PATHS.map((d, i) => (
          <React.Fragment key={d}>
            <path d={d} className={i % 3 === 1 ? "hl-ft-cur hl-ft-alt" : "hl-ft-cur"} />
            <path d={d} className="hl-ft-cur hl-ft-core" />
          </React.Fragment>
        ))}
      </g>
      <path className="hl-ft-cereb" d={CEREB} />
      <path className="hl-ft-stem" d={STEM} />
    </svg>
  );
}

export default memo(AiBrain);
